import React, { useState } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2, AlertTriangle } from 'lucide-react';
import {
    stcZoneService,
    StcZoneDto,
    BulkUpdateStcZoneRequest
} from '../../../../../services/stcZoneService';
import { Warehouse } from '../../../../../types/warehouse';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ZoneBulkUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedZoneIds: number[];
    data: StcZoneDto[];
    onUpdateSuccess: () => void;
    onDeleteSuccess: () => void;
    warehouses: Warehouse[];
}

interface UpdateFields {
    stcZoneID: boolean;
    warehouseID: boolean;
    description1: boolean;
}

export const ZoneBulkUpdateModal: React.FC<ZoneBulkUpdateModalProps> = ({
    isOpen,
    onClose,
    selectedZoneIds,
    data,
    onUpdateSuccess,
    onDeleteSuccess,
    warehouses
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'edit' | 'delete'>('edit');

    const selectedZones = data.filter(z => selectedZoneIds.includes(z.id));
    const selectedCount = selectedZones.length;
    const isSingleSelection = selectedCount === 1;
    const singleZone = isSingleSelection ? selectedZones[0] : null;

    // Edit state - pre-fill when single selection
    const [updateFields, setUpdateFields] = useState<UpdateFields>({
        stcZoneID: isSingleSelection,
        warehouseID: isSingleSelection,
        description1: isSingleSelection
    });
    const [stcZoneID, setStcZoneID] = useState(singleZone?.stcZoneID ?? '');
    const [warehouseID, setWarehouseID] = useState(singleZone?.warehouseID ?? '');
    const [description1, setDescription1] = useState(singleZone?.description1 ?? '');

    // Operation state
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const enabledFieldCount = Object.values(updateFields).filter(Boolean).length;

    const handleBulkUpdate = async () => {
        try {
            setIsSaving(true);
            setError('');

            if (enabledFieldCount === 0) {
                setError(t('manager.modules.adminResources.zoneBulk.errorSelectField', {}, 'Velg minst ett felt å oppdatere'));
                return;
            }

            if (updateFields.stcZoneID && selectedCount > 1) {
                setError(t('manager.modules.adminResources.zoneBulk.errorZoneIdSingleOnly', {}, 'Sone ID kan kun endres for én sone om gangen'));
                return;
            }

            if (updateFields.warehouseID && selectedCount > 1) {
                setError(t('manager.modules.adminResources.zoneBulk.errorWarehouseIdSingleOnly', {}, 'Lager ID kan kun endres for én sone om gangen'));
                return;
            }

            const bulkUpdateRequests: BulkUpdateStcZoneRequest[] = selectedZones.map(item => {
                const request: BulkUpdateStcZoneRequest = {
                    companyNo: item.companyNo,
                    warehouseID: item.warehouseID,
                    stcZoneID: item.stcZoneID,
                    description1: item.description1
                };

                if (updateFields.stcZoneID) {
                    request.stcZoneID = stcZoneID;
                }
                if (updateFields.warehouseID) {
                    request.warehouseID = warehouseID;
                }
                if (updateFields.description1) {
                    request.description1 = description1;
                }

                return request;
            });

            await stcZoneService.bulkUpdate(bulkUpdateRequests);

            setShowSuccess(true);
            setShowUpdateConfirm(false);
            setTimeout(() => {
                setShowSuccess(false);
                onUpdateSuccess();
            }, 1500);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminResources.common.errorUpdate', {}, 'Feil ved oppdatering'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkDelete = async () => {
        try {
            setIsDeleting(true);
            setError('');

            const idsToDelete = selectedZones.map(item => item.id);
            await stcZoneService.bulkDelete(idsToDelete);

            setShowSuccess(true);
            setShowDeleteConfirm(false);
            setTimeout(() => {
                setShowSuccess(false);
                onDeleteSuccess();
            }, 1500);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminResources.common.errorDelete', {}, 'Feil ved sletting'));
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    const renderFieldEditor = (
        fieldKey: keyof UpdateFields,
        label: string,
        value: string,
        onChange: (val: string) => void,
        placeholder: string,
        singleOnly?: boolean
    ) => (
        <div className={`border rounded-lg p-4 transition-colors ${
            updateFields[fieldKey] ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
        }`}>
            <div className="flex items-center gap-2 mb-2">
                <input
                    type="checkbox"
                    id={`field-${fieldKey}`}
                    checked={updateFields[fieldKey]}
                    onChange={(e) => setUpdateFields(prev => ({ ...prev, [fieldKey]: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor={`field-${fieldKey}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                    {label}
                </label>
            </div>
            {updateFields[fieldKey] && (
                <div className="mt-2 space-y-2">
                    {singleOnly && selectedCount > 1 && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2">
                            <p className="text-xs text-amber-700">
                                <AlertTriangle className="w-3 h-3 inline mr-1" />
                                {t('manager.modules.adminResources.zoneBulk.fieldSingleOnlyWarning', { '0': label }, `${label} kan kun endres for én sone om gangen.`)}
                            </p>
                        </div>
                    )}
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={placeholder}
                        disabled={singleOnly && selectedCount > 1}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">{t('manager.modules.adminResources.zoneBulk.title', {}, 'Vedlikehold Lagersoner')}</h2>
                            <p className="text-sm text-blue-100">
                                {selectedCount} {selectedCount === 1 ? t('manager.modules.adminResources.zoneBulk.zoneSingular', {}, 'sone') : t('manager.modules.adminResources.zoneBulk.zonePlural', {}, 'soner')} {t('manager.modules.adminResources.resourceBulk.selectedSuffix', {}, 'valgt')}
                            </p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('edit')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'edit'
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            {t('common.buttons.edit', {}, 'Rediger')}
                        </button>
                        <button
                            onClick={() => setActiveTab('delete')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'delete'
                                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            {t('common.buttons.delete', {}, 'Slett')}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">
                                        {activeTab === 'edit' ? t('manager.modules.adminResources.common.updateComplete', {}, 'Oppdatering fullført!') : t('manager.modules.adminResources.common.deleteComplete', {}, 'Sletting fullført!')}
                                    </p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'edit' ? (
                            <>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-blue-800">
                                            <p className="font-medium mb-1">{t('manager.modules.adminResources.common.bulkUpdateHeading', {}, 'Masseoppdatering')}</p>
                                            <p>{t('manager.modules.adminResources.zoneBulk.bulkUpdateInfo', {}, 'Velg feltene du ønsker å oppdatere. Kun avkryssede felt vil bli endret for alle valgte soner.')}</p>
                                        </div>
                                    </div>
                                </div>

                                {renderFieldEditor('stcZoneID', t('manager.modules.adminResources.zone.zoneIdLabel', {}, 'Sone ID'), stcZoneID, setStcZoneID, t('manager.modules.adminResources.zoneBulk.zoneIdPlaceholder', {}, 'Ny sone ID'), true)}
                                {/* Lagersted - dropdown */}
                                <div className={`border rounded-lg p-4 transition-colors ${
                                    updateFields.warehouseID ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                                }`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <input
                                            type="checkbox"
                                            id="field-warehouseID"
                                            checked={updateFields.warehouseID}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, warehouseID: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="field-warehouseID" className="text-sm font-medium text-gray-700 cursor-pointer">
                                            {t('manager.modules.adminResources.zone.warehouseLabel', {}, 'Lagersted')}
                                        </label>
                                    </div>
                                    {updateFields.warehouseID && (
                                        <div className="mt-2 space-y-2">
                                            {selectedCount > 1 && (
                                                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                                                    <p className="text-xs text-amber-700">
                                                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                                                        {t('manager.modules.adminResources.zoneBulk.warehouseSingleOnlyWarning', {}, 'Lagersted kan kun endres for én sone om gangen.')}
                                                    </p>
                                                </div>
                                            )}
                                            <select
                                                value={warehouseID}
                                                onChange={(e) => setWarehouseID(e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                                disabled={selectedCount > 1}
                                            >
                                                <option value="">{t('manager.modules.adminResources.zone.selectWarehouseOption', {}, '-- Velg lagersted --')}</option>
                                                {warehouses.map(w => (
                                                    <option key={w.warehouseID} value={w.warehouseID}>
                                                        {w.warehouseDescr}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                {renderFieldEditor('description1', t('common.fields.description', {}, 'Beskrivelse'), description1, setDescription1, t('manager.modules.adminResources.zoneBulk.descriptionPlaceholder', {}, 'Ny beskrivelse for alle valgte soner'))}

                                {enabledFieldCount > 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                            <div className="text-sm text-green-800">
                                                <p className="font-medium">{t('manager.modules.adminResources.common.readyToSave', {}, 'Klar til å lagre')}</p>
                                                <p>{t('manager.modules.adminResources.zoneBulk.fieldsWillUpdate', { '0': enabledFieldCount, '1': selectedCount, '2': selectedCount === 1 ? t('manager.modules.adminResources.zoneBulk.zoneSingular', {}, 'sone') : t('manager.modules.adminResources.zoneBulk.zonePlural', {}, 'soner') }, `${enabledFieldCount} felt vil bli oppdatert for ${selectedCount} ${selectedCount === 1 ? 'sone' : 'soner'}`)}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showUpdateConfirm && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-amber-800">{t('manager.modules.adminResources.common.confirmUpdateTitle', {}, 'Bekreft oppdatering')}</p>
                                                <p className="text-sm text-amber-700 mt-1">
                                                    {t('manager.modules.adminResources.zoneBulk.confirmUpdateQuestion', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.zoneBulk.zoneSingular', {}, 'sone') : t('manager.modules.adminResources.zoneBulk.zonePlural', {}, 'soner') }, `Er du sikker på at du vil oppdatere ${selectedCount} ${selectedCount === 1 ? 'sone' : 'soner'}?`)}
                                                </p>
                                                <div className="mt-2 text-xs text-amber-700">
                                                    <p className="font-medium">{t('manager.modules.adminResources.zoneBulk.affectedZones', {}, 'Berørte soner:')}</p>
                                                    <div className="max-h-20 overflow-y-auto mt-1 space-y-0.5">
                                                        {selectedZones.slice(0, 20).map(z => (
                                                            <div key={z.id}>{z.stcZoneID} ({z.warehouseID}): {z.description1}</div>
                                                        ))}
                                                        {selectedZones.length > 20 && (
                                                            <div className="italic">{t('manager.modules.adminResources.common.andMore', { '0': selectedZones.length - 20 }, `...og ${selectedZones.length - 20} til`)}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 mt-3">
                                                    <button onClick={handleBulkUpdate} disabled={isSaving} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                                        {isSaving ? t('common.status.saving', {}, 'Lagrer...') : t('manager.modules.adminResources.common.yesUpdate', {}, 'Ja, oppdater')}
                                                    </button>
                                                    <button onClick={() => setShowUpdateConfirm(false)} className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                                                        {t('common.buttons.cancel', {}, 'Avbryt')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-red-800">
                                            <p className="font-medium mb-1">{t('manager.modules.adminResources.common.bulkDeleteHeading', {}, 'Massesletting')}</p>
                                            <p>{t('manager.modules.adminResources.zoneBulk.bulkDeleteInfo', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.zoneBulk.zoneSingular', {}, 'sone') : t('manager.modules.adminResources.zoneBulk.zonePlural', {}, 'soner') }, `Du er i ferd med å slette ${selectedCount} ${selectedCount === 1 ? 'sone' : 'soner'}. Denne handlingen kan ikke angres.`)}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <h3 className="text-sm font-medium text-gray-900">{t('manager.modules.adminResources.zoneBulk.zonesToDelete', {}, 'Soner som vil bli slettet:')}</h3>
                                    <div className="max-h-48 overflow-y-auto space-y-1">
                                        {selectedZones.map(item => (
                                            <div key={item.id} className="text-xs bg-gray-50 p-2 rounded border border-gray-200">
                                                <span className="font-medium">{item.stcZoneID}</span> ({item.warehouseID}): {item.description1}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {showDeleteConfirm && (
                                    <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-red-800">{t('manager.modules.adminResources.common.areYouSure', {}, 'Er du helt sikker?')}</p>
                                                <p className="text-sm text-red-700 mt-1">
                                                    {t('manager.modules.adminResources.zoneBulk.permanentDeleteWarning', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.zoneBulk.zoneSingular', {}, 'sone') : t('manager.modules.adminResources.zoneBulk.zonePlural', {}, 'soner') }, `Denne handlingen kan ikke angres. ${selectedCount} ${selectedCount === 1 ? 'sone' : 'soner'} vil bli permanent slettet.`)}
                                                </p>
                                                <div className="flex gap-2 mt-3">
                                                    <button onClick={handleBulkDelete} disabled={isDeleting} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                                                        {isDeleting ? t('manager.modules.adminResources.common.deleting', {}, 'Sletter...') : t('manager.modules.adminResources.common.yesDeletePermanent', {}, 'Ja, slett permanent')}
                                                    </button>
                                                    <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                                                        {t('common.buttons.cancel', {}, 'Avbryt')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
                        <div className="flex items-center justify-between gap-3">
                            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                {t('common.buttons.close', {}, 'Lukk')}
                            </button>

                            {activeTab === 'edit' ? (
                                <button
                                    onClick={() => setShowUpdateConfirm(true)}
                                    disabled={enabledFieldCount === 0 || isSaving || showUpdateConfirm}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        enabledFieldCount === 0 || isSaving || showUpdateConfirm
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                >
                                    <Save className="w-4 h-4" />
                                    {t('manager.modules.adminResources.zoneBulk.updateButton', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.zoneBulk.zoneSingular', {}, 'sone') : t('manager.modules.adminResources.zoneBulk.zonePlural', {}, 'soner') }, `Oppdater ${selectedCount} ${selectedCount === 1 ? 'sone' : 'soner'}`)}
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isDeleting || showDeleteConfirm}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        isDeleting || showDeleteConfirm
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-red-600 text-white hover:bg-red-700'
                                    }`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {t('manager.modules.adminResources.zoneBulk.deleteButton', { '0': selectedCount, '1': selectedCount === 1 ? t('manager.modules.adminResources.zoneBulk.zoneSingular', {}, 'sone') : t('manager.modules.adminResources.zoneBulk.zonePlural', {}, 'soner') }, `Slett ${selectedCount} ${selectedCount === 1 ? 'sone' : 'soner'}`)}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
